/* ruleValidate — ontology-grounded AST validation.
 *
 * Spec 07 §"Builder data flow". Every check is keyed off
 * docs/ontology/codegen.json so we never drift from the typed
 * ontology shipped to the agent.
 *
 * Per CLAUDE.md §5: errors are returned, not thrown. The UI surfaces
 * them on the validator strip and disables Promote until ok=true.
 */

import type { Predicate, ExtendedTerm, NodePattern, AggTerm, Term } from "../types/rule";
import codegen from "../../../docs/ontology/codegen.json";

export interface ValidationIssue {
  severity: "error" | "warning";
  /** Short, designer-readable message. */
  message: string;
  /** Optional code so the UI can highlight which node card lit up. */
  path?: string;
}
export interface ValidationResult {
  ok: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

interface FieldSpec { path: string; kind: string; field: string; type: "number" | "string"; unit?: string }
const FIELDS: ReadonlyArray<FieldSpec> = (codegen.fields ?? []) as ReadonlyArray<FieldSpec>;
const NODE_KINDS: ReadonlySet<string> = new Set(Object.keys(codegen.nodes ?? {}));
const EDGE_KINDS: ReadonlySet<string> = new Set((codegen.edges ?? []) as string[]);
const FIELD_MAP: ReadonlyMap<string, FieldSpec> = new Map(FIELDS.map((f) => [f.path, f]));

function termType(t: ExtendedTerm): "number" | "string" | "boolean" | "unknown" {
  if (t.kind === "lit")   return t.type;
  if (t.kind === "field") {
    const f = FIELD_MAP.get(t.path);
    return f ? f.type : "unknown";
  }
  if (t.op === "count") return "number";
  return "number";
}

function termPath(t: Term | ExtendedTerm): string | undefined {
  return t.kind === "field" ? t.path : undefined;
}

function checkField(t: ExtendedTerm, out: ValidationIssue[]): void {
  if (t.kind !== "field") return;
  if (!FIELD_MAP.has(t.path)) {
    const cls = t.path.split(".")[0];
    out.push({
      severity: "error",
      message: `Unknown field "${t.path}". Pick one of the ${cls ?? "ontology"} fields from the palette.`,
      path: t.path,
    });
  }
}

function checkPattern(np: NodePattern, out: ValidationIssue[]): void {
  if (!NODE_KINDS.has(np.nodeKind)) {
    out.push({
      severity: "error",
      message: `Unknown node kind "${np.nodeKind}". The ontology has ${NODE_KINDS.size} classes — drag one from the left rail.`,
    });
  }
}

function checkRelation(rel: string, out: ValidationIssue[]): void {
  if (!EDGE_KINDS.has(rel)) {
    out.push({
      severity: "error",
      message: `Unknown relation "${rel}". Pick one from the ontology — it must be in codegen.json edges.`,
    });
  }
}

function checkAgg(a: AggTerm, out: ValidationIssue[]): void {
  checkPattern(a.over, out);
  if (a.op !== "count") {
    if (!a.field) {
      out.push({ severity: "error", message: `Aggregation ${a.op} needs a field. Drag a number-typed field onto it.` });
    } else {
      checkField(a.field, out);
    }
  }
  if (a.via) {
    checkRelation(a.via.relation, out);
    checkPattern(a.via.target, out);
  }
}

function checkComparableTypes(lhs: ExtendedTerm, rhs: ExtendedTerm, out: ValidationIssue[]): void {
  const lt = termType(lhs);
  const rt = termType(rhs);
  if (lt === "unknown" || rt === "unknown") return; // already flagged by checkField
  if (lt !== rt) {
    const path = termPath(lhs) ?? termPath(rhs);
    const issue: ValidationIssue = {
      severity: "error",
      message: `Type mismatch: comparing ${lt} with ${rt}. Adjust the right side or pick a different field.`,
      ...(path !== undefined ? { path } : {}),
    };
    out.push(issue);
  }
}

function walk(p: Predicate, errors: ValidationIssue[], warnings: ValidationIssue[]): void {
  switch (p.kind) {
    case "cmp": {
      if (p.lhs.kind === "agg") checkAgg(p.lhs, errors); else checkField(p.lhs, errors);
      if (p.rhs.kind === "agg") checkAgg(p.rhs, errors); else checkField(p.rhs, errors);
      checkComparableTypes(p.lhs, p.rhs, errors);
      // Tautology / unsat shortcuts on identical-path comparisons.
      if (p.lhs.kind === "field" && p.rhs.kind === "field" && p.lhs.path === p.rhs.path) {
        if (p.op === "==" || p.op === ">=" || p.op === "<=") {
          warnings.push({ severity: "warning", message: `Tautology: ${p.lhs.path} ${p.op} ${p.lhs.path} is always true.` });
        }
        if (p.op === ">" || p.op === "<" || p.op === "!=") {
          warnings.push({ severity: "warning", message: `Unsatisfiable: ${p.lhs.path} ${p.op} ${p.lhs.path} is never true.` });
        }
      }
      return;
    }
    case "in_range":
    case "not_in_range": {
      if (p.value.kind === "agg") checkAgg(p.value, errors); else checkField(p.value, errors);
      if (!(p.range.lo < p.range.hi)) {
        errors.push({ severity: "error", message: `Range must have lo < hi (got [${p.range.lo}, ${p.range.hi}]).` });
      }
      return;
    }
    case "in_set":
    case "not_in_set": {
      if (p.value.kind === "agg") checkAgg(p.value, errors); else checkField(p.value, errors);
      if (p.set.values.length === 0) {
        errors.push({ severity: "error", message: `Set is empty — pick at least one value.` });
      }
      // Element-type / field-type alignment.
      const vt = termType(p.value);
      if (vt !== "unknown" && vt !== p.set.type) {
        errors.push({
          severity: "error",
          message: `Set type ${p.set.type} does not match value type ${vt}.`,
        });
      }
      return;
    }
    case "graph_pattern":
    case "exists":
    case "forbidden": {
      const subj = p.kind === "graph_pattern" ? p.subject : p.pattern.subject;
      const rel  = p.kind === "graph_pattern" ? p.relation : p.pattern.relation;
      const obj  = p.kind === "graph_pattern" ? p.object   : p.pattern.object;
      checkPattern(subj, errors);
      checkRelation(rel, errors);
      if (obj.kind === "pattern") checkPattern(obj, errors);
      // obj can also be a Literal (kind: "lit") — nothing to validate
      // there; type-coercion was already enforced by the AST schema.
      return;
    }
    case "and":
    case "or":
      walk(p.left, errors, warnings);
      walk(p.right, errors, warnings);
      return;
    case "not":
      walk(p.inner, errors, warnings);
      return;
  }
}

export function validateRule(p: Predicate): ValidationResult {
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];
  walk(p, errors, warnings);
  return { ok: errors.length === 0, errors, warnings };
}
